import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { KeyboardArrowDown } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Grid, Box, Typography, Stack, MenuItem, styled, Button, Divider } from '@mui/material';
import { MilestoneModel, MultipleMilestoneModel, Toast } from 'app/components';
import { Switch } from 'app/components/v2';
import { Container, CustomSelect, Wrapper } from './style';
import { getMilestones } from 'app/store/features/chartsSlice';
import {
  getComplianceData,
  getDeviationsPerDataSource,
  getDeviationsPerInfoType,
  getObjectsAndDeviations,
  getTrendsData,
} from 'app/store/features/complianceSlice';
import { getPolicyById, setPrivacyPersistedError } from 'app/store/features/privacyPersistedSlice';
import { getAllPolicies, setPrivacyError } from 'app/store/features/privacySlice';
import { setMileStonesDuration, setShowMileStone } from 'app/store/features/toggleSidebarSlice';
import { ColorsV2 } from 'app/constants/color';
import apiService from 'app/services/apiService';
import {
  arbitraryLinePlugin,
  formatNumber,
  getMenuItems,
  parseAndFormatDate,
  returnDefaultValues,
} from 'app/utils';
import ComplianceLevelLineChart from './ComplianceLevelLineChart';
import DataSourceSection from './DataSourceSection';
import InformationClassSection from './InformationClassSection';
import ObjectsAndDeviations from './ObjectsAndDeviations';
import OverAllCompliance from './OverAllCompliance';
import PolicyViolationChart from './PolicyViolationChart';
import ViolationExportMenu from './violationexport/ViolationExportMenu';
import { fetchUserSettings } from 'app/store/features/userSettingsSlice';
import { Buttons, Containers } from 'app/constants/sizing';
import PageTabs from 'app/components/v2/PageTabs/PageTabs';

const Compliance = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [toast, setToast] = useState({
    open: false,
    message: '',
    severity: '',
  });

  const [structuredAndUnstructuredData, setStructuredAndUnstructuredData] = useState({
    structured: null,
    unstructured: null,
    data: [],
  });

  const withoutTemplate = useSelector(state => state.privacyPersisted.WithoutTemplate);

  const [isMilestoneModelOpen, setIsMilestoneModelOpen] = useState(false);
  const [fetchedMultipleMilestoneData, setFetchedMultipleMilestoneData] = useState([]);
  const [multipleMilestoneData, setMultipleMilestoneData] = useState([]);
  const [isMultipleMilestoneModelOpen, setIsMultipleMilestoneModelOpen] = useState(false);
  const [policyData, setPolicyData] = useState({});
  const [policyNumber, setPolicyNumber] = useState(0);
  const [policyName, setPolicyName] = useState("Policy 1");

  const { t } = useTranslation('translation', { keyPrefix: 'compliance' });

  const allPolicies = useSelector(state => state.privacy.allPolicies);
  const privacyError = useSelector(state => state.privacy.privacyError);
  const {
    compliance: { complianceRates = [] },
  } = useSelector(state => state.compliance.complianceData);
  const {
    trends: { dataClassLevels = [] },
  } = useSelector(state => state.compliance.trendsData);
  const showMileStone = useSelector(state => state.toggleSidebar.showMileStone);
  const mileStonesDuration = useSelector(state => state.toggleSidebar.mileStonesDuration);
  const privacyPersistedError = useSelector(state => state.privacyPersisted.privacyPersistedError);

  const {
    summed: { totNrOfObjects = [], totNrOfDeviations = [] },
  } = useSelector(state => state.compliance.objectsAndDeviations);

  const { milestones = [] } = useSelector(state => state.charts);

  const totalIncreaseInDeviations =
    totNrOfDeviations[totNrOfDeviations?.length - 1]?.nrOfDeviations -
      totNrOfDeviations[totNrOfDeviations?.length - 2]?.nrOfDeviations || 0;
  const totalIncreaseInObjects =
    totNrOfObjects[totNrOfObjects?.length - 1]?.nrOfObjects -
      totNrOfObjects[totNrOfObjects?.length - 2]?.nrOfObjects || 0;
  const percentageIncreaseInDeviations =
    (totalIncreaseInDeviations / totNrOfDeviations[totNrOfDeviations?.length - 1]?.nrOfDeviations ||
      0) * 100;
  const percentageIncreaseInObjects =
    (totalIncreaseInObjects / totNrOfObjects[totNrOfObjects?.length - 1]?.nrOfObjects || 0) * 100;

  let minDate = new Date();

  const textFieldRef = useRef(null);
  const boxRef = useRef(null);

  dataClassLevels
    .map(classLevel => classLevel.dataPoints)
    .forEach(array => {
      array.forEach(obj => {
        const currentDate = new Date(obj.date);
        if (currentDate < minDate) {
          minDate = currentDate;
        }
      });
    });

  const resetToast = () => {
    dispatch(setPrivacyError(null));
    dispatch(setPrivacyPersistedError(null));
    setToast({
      open: false,
      message: '',
      severity: '',
    });
  };

  const fetchData = async () => {
    const [structuredRes, unstructuredRes] = await Promise.all([
      apiService.structuredChartData('/structured'),
      apiService.structuredChartData('/unstructured'),
    ]);
    setStructuredAndUnstructuredData({
      structured: structuredRes?.data?.structured,
      unstructured: unstructuredRes?.data?.unstructured,
      data: structuredRes?.data?.structured?.datasources?.concat(
        unstructuredRes?.data?.unstructured?.datasources
      ),
    });
  };

  const getPolicy = async () => {
    const infoObjectsArr = [];
    const protectionAspectsArr = [];
    const restrictionsArr = [];

    const { data } = await apiService.getPolicyById({
      id: withoutTemplate?.policyType,
      readOnly: true,
    });
    const newPolicyData = data?.Data?.levels;
    setPolicyName(data?.name);
    setPolicyData(data?.Data);

    newPolicyData?.forEach(data => {
      data.infoObjects?.forEach(info => {
        infoObjectsArr.push(info);
      });

      data.protectionAspects?.forEach(protection => {
        if (!protectionAspectsArr.some(val => val.id === protection.id)) {
          protectionAspectsArr.push(protection);
        }
      });
    });

    protectionAspectsArr?.forEach(data => {
      data.restrictions?.forEach(restriction => {
        if (!restrictionsArr.some(val => val.id === restriction.id)) {
          restrictionsArr.push(restriction);
        }
      });
    });
  };

  useEffect(() => {
    const fetchComplianceData = async () => {
      dispatch(getComplianceData({ policyTypeId: 'uuid-for-icah-policy-type', query: '' }));
      dispatch(getTrendsData({ policyTypeId: 'uuid-for-icah-policy-type', query: '' }));
      dispatch(getObjectsAndDeviations({ policyTypeId: 'uuid-for-icah-policy-type', query: '' }));
      dispatch(getMilestones());
      dispatch(
        getDeviationsPerDataSource({
          policyTypeId: 'uuid-for-icah-policy-type',
          payload: {
            datasources: [],
          },
        })
      );
      dispatch(
        getDeviationsPerInfoType({
          policyTypeId: 'uuid-for-icah-policy-type',
          payload: {
            classLevels: [],
          },
        })
      );
    };

    if (!allPolicies?.id) {
      dispatch(getAllPolicies());
    } else {
      fetchComplianceData();
    }
  }, [allPolicies]);

  useEffect(() => {
    fetchData();
    getPolicy();
    dispatch(fetchUserSettings());
  }, []);

  // useEffect(() => {
  //   const fetchData = async () => {
  //     try {
  //       dispatch(getEstimatedDeviationsReportSize({
  //         payload: {
  //           data: payloadForEstimatedDeviations
  //         }
  //       }));
  //     } catch (error) {
  //       console.error('Error fetching data:', error);
  //     }
  //   };
  //   fetchData();
  // }, [
  //   // dispatch, structuredAndUnstructuredData, informationClasses, informationTypes, restrictions
  //   ]);

  // const fetchEstimatedDeviations = (payloadForEstimatedDeviations) => {
  //   try {
  //     dispatch(getEstimatedDeviationsReportSize({
  //       payload: {
  //         data: payloadForEstimatedDeviations
  //       }
  //     }));
  //   } catch (error) {
  //     console.error('Error fetching data:', error);
  //   }
  // }

  useEffect(() => {
    const handleInputChange = () => {
      const inputValue = textFieldRef.current.value;
      if (inputValue.length > 50) {
        textFieldRef.current.value = inputValue.slice(0, 50);
        setToast({
          open: true,
          message: t('export_text_field_restriction'),
          severity: 'error',
        });
      }
    };

    textFieldRef?.current?.addEventListener('input', handleInputChange);

    return () => {
      textFieldRef?.current?.removeEventListener('input', handleInputChange);
    };
  }, []);

  useEffect(() => {
    if (privacyError) {
      setToast({
        open: true,
        message: privacyError.hint || 'Something Went Wrong',
        severity: 'error',
      });
    }

    if (privacyPersistedError) {
      setToast({
        open: true,
        message: privacyPersistedError.hint || 'Something Went Wrong',
        severity: 'error',
      });
    }
  }, [privacyError, privacyPersistedError]);

  useEffect(() => {
    if (!fetchedMultipleMilestoneData.length) {
      setIsMultipleMilestoneModelOpen(false);
      setIsMilestoneModelOpen(false);
    } else if (fetchedMultipleMilestoneData.length > 1) {
      setIsMultipleMilestoneModelOpen(true);
    } else {
      setIsMilestoneModelOpen(true);
    }
  }, [fetchedMultipleMilestoneData]);

  const StyledContainer = styled(Box)({
    padding: '0.75em',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  });

  const FindingChildGrid = styled(Grid)({
    background: ColorsV2.primary,
    color: 'white',
    fontFamily: 'DM Sans,sans-serif',
    padding: '0.7em',
    boxSizing: 'border-box',
  });

  const handleView = () => {
    dispatch(getPolicyById({ id: allPolicies.policyType, readOnly: true })).then(res => {
      if (!res.error) {
        if (allPolicies.templateId) {
          navigate('/board/template', { state: { action: 'VIEW' } });
        } else {
          navigate('/board/blank', { state: { action: 'VIEW' } });
        }
      }
    });
  };
  const handleChange = (name, value) => {
    const updatedData = { ...showMileStone, [name]: value };
    dispatch(setShowMileStone(updatedData));
  };

  const handleDurationChange = event => {
    const updatedData = { ...mileStonesDuration, [event?.target?.name]: event.target.value };
    dispatch(setMileStonesDuration(updatedData));
  };
  const formattedData = milestones?.map(obj => ({
    ...obj,
    date: parseAndFormatDate(obj.date),
    pointY: 23,
  }));
  const MileStones = arbitraryLinePlugin(formattedData, setFetchedMultipleMilestoneData, 'v2');

  const handleExportView = () => {
    boxRef.current.scrollIntoView({
      block: 'nearest',
      inline: 'center',
      behavior: 'smooth',
      alignToTop: false,
    });
  };

  const handleSwapPolicy = (_, value) => {
    setPolicyNumber(value);
  }

  return (
    <Box>
      <Container sx={{backgroundColor: ColorsV2.secondary}}>
        <Wrapper>
          <Typography
          variant='h4'
          noWrap
          sx={{
            color: ColorsV2.white,
            marginBottom: '20px'
          }}
          >
            {t('page_heading')}
          </Typography>
          <Stack
            display={'flex'}
            justifyContent={'space-between'}
            width={'100%'}
            alignItems={'çenter'}
          >
          </Stack>
          <PageTabs
            value={policyNumber}
            onChange={handleSwapPolicy}
            tabs={[
              {value: 0, label: policyName, props: {sx: {backgroundColor: ColorsV2.secondary, "&.Mui-selected": {backgroundColor: ColorsV2.primary}}}},
              ]} />
          <StyledContainer sx={{backgroundColor: ColorsV2.primary, padding: '20px', borderRadius: '0px 4px 4px 4px', marginBottom: '20px'}}>
          <Stack
            direction='row'
            justifyContent={'flex-end'}
            alignItems={'center'}
            gap={1}
            sx={{
              width: '100%',
              backgroundColor: ColorsV2.primary,
            }}
            >
              <Button
                name={'IconButton'}
                startIcon={
                  <VisibilityIcon style={{ fontSize: '0.9em', color: ColorsV2.white }} />
                }
                sx={{
                  ...Buttons.baseStyle.primaryButtonLowPriority,
                }}
                onClick={handleView}
                disabled={!allPolicies?.policyType}
              >
                {t('view_policy')}
              </Button>
              <Button
                name={'IconButton'}
                onClick={handleExportView}
                startIcon={<UploadIcon style={{ fontSize: '0.9em', color: ColorsV2.white }} />}
                sx={{
                  ...Buttons.baseStyle.primaryButtonLowPriority,
                }}
              >
                {t('export_data')}
              </Button>
              <Button
                disabled
                name={'IconButton'}
                startIcon={<DownloadIcon style={{ fontSize: '0.9em', color: ColorsV2.white }} />}
                sx={{
                  ...Buttons.baseStyle.primaryButtonLowPriority,
                }}
              >
                {t('generate_Report')}
              </Button>
            </Stack>
            <Grid container gap={2} sx={{...Containers.shadowSubtleAcent}}>
              <Grid
                item
                xs={4.5}
              >
                <OverAllCompliance />
              </Grid>
              <Grid
                item
                xs={7}
              >
                <Stack
                  direction='column'
                  alignItems={'flex-start'}
                  padding={'0.5em'}
                  backgroundColor={ColorsV2.primary}
                >
                  <Typography variant='h6' sx={{color: ColorsV2.white}}>
                    {t('compliance_over_time')}
                  </Typography>
                  <Stack direction='row' alignItems='center' sx={{alignSelf: 'flex-start'}} >
                    <Typography variant='body2' sx={{color: ColorsV2.textInfo, marginRight: '10px'}}>
                      {t('milestones')}
                    </Typography>
                    <Switch
                      name='complianceLevel'
                      checked={showMileStone.complianceLevel}
                      handlechange={() =>
                        handleChange('complianceLevel', !showMileStone.complianceLevel)
                      }
                    />

                    <Typography variant='body2' sx={{color: ColorsV2.textInfo, marginLeft: '20px'}}>
                      {t('period')}
                    </Typography>
                    <CustomSelect
                      name='complianceLevel'
                      defaultValue={returnDefaultValues(
                        new Date(),
                        complianceRates.map(item => new Date(item?.date))[0]
                      )}
                      value={mileStonesDuration.complianceLevel}
                      onChange={handleDurationChange}
                      IconComponent={KeyboardArrowDown}
                      inputProps={{
                        'aria-label': 'Language Selector',
                        MenuProps: {
                          PaperProps: {
                            sx: {
                              backgroundColor: ColorsV2.secondary,
                              color: ColorsV2.white,
                              border: `2px solid ${ColorsV2.outline} `,
                            },
                          },
                        },
                      }}
                    >
                      {getMenuItems(
                        new Date(),
                        complianceRates.map(item => new Date(item?.date))[0]
                      ).map(item => (
                        <MenuItem key={item.label} value={item.value} sx={{ fontSize: '0.7em' }}>
                          {item.label}
                        </MenuItem>
                      ))}
                    </CustomSelect>
                    <Button
                      name='IconButton'
                      onClick={() => setIsMilestoneModelOpen(true)}
                      startIcon={<AddIcon style={{ fontSize: '0.9em', color: ColorsV2.white }} />}
                      sx={{
                          ...Buttons.baseStyle.primaryButtonLowPriority,
                          height: '30px',
                          fontSize: '0.8rem',
                          marginLeft: '20px',
                          boxShadow: 0,
                      }}
                    >
                      {t('AddMilestone')}
                    </Button>
                  </Stack>
                </Stack>
                <Box paddingTop={2}>
                  <ComplianceLevelLineChart
                    MileStones={showMileStone.complianceLevel ? MileStones : []}
                    duration={mileStonesDuration.complianceLevel}
                  />
                </Box>
              </Grid>
            </Grid>
            <FindingChildGrid>
              <Typography variant='h5' sx={{color: ColorsV2.white}}>
                {t('information_objects')}
              </Typography>
            </FindingChildGrid>
            <Stack direction='column' sx={{...Containers.shadowSubtleAcent}}>
              <Grid container gap={1}>
                <Grid
                  item
                  xs={4.8}
                >
                  <Grid>
                    <FindingChildGrid item xs={12}>
                      <Typography variant='h6' sx={{color: ColorsV2.white}}>
                        {t('objects_found')}
                      </Typography>
                    </FindingChildGrid>
                    <Box p={0.8}>
                      <Typography fontWeight={'normal'} fontSize={'0.7em'} color={ColorsV2.white}>
                        {formatNumber(totNrOfObjects[totNrOfObjects?.length - 1]?.nrOfObjects || 0)}
                      </Typography>
                      <Typography color={ColorsV2.white} fontSize={'0.7em'}>
                        {t('increase')} {`${formatNumber(totalIncreaseInObjects)}`}
                        <span style={{ color: ColorsV2.acent }}>
                          {' '}
                          ({percentageIncreaseInObjects.toFixed(2)}% &#x25B2;){' '}
                        </span>
                      </Typography>
                    </Box>
                    <Box maxHeight={'13vh'} p={1}>
                      <ObjectsAndDeviations type={'OBJECTS'} />
                    </Box>
                  </Grid>
                  <Grid mt={2}>
                    <FindingChildGrid item xs={12}>
                      <Typography fontSize={'0.8em'} fontWeight={'normal'} color={'white'}>
                        {t('policy_violations')}
                      </Typography>
                    </FindingChildGrid>
                    <Box p={0.8}>
                      <Typography fontWeight={'normal'} fontSize={'0.7em'} color={ColorsV2.white}>
                        {formatNumber(
                          totNrOfDeviations[totNrOfDeviations?.length - 1]?.nrOfDeviations || 0
                        )}
                      </Typography>
                      <Typography color={ColorsV2.white} fontSize={'0.7em'}>
                        {t('increase')} {`${formatNumber(totalIncreaseInDeviations)}`}
                        <span style={{ color: ColorsV2.acent }}>
                          {' '}
                          ({percentageIncreaseInDeviations.toFixed(2)}% &#x25B2;){' '}
                        </span>
                      </Typography>
                    </Box>
                    <Box maxHeight={'13vh'} p={1}>
                      <ObjectsAndDeviations />
                    </Box>
                  </Grid>
                </Grid>
                <Grid
                  item
                  xs={7}
                >
                  <Grid container p='0.45em' backgroundColor={ColorsV2.primary}>
                    <Grid pl='1%' mr='auto' my={'auto'} boxSizing='border-box'>
                      <Typography variant='h6' sx={{color: ColorsV2.white}}>
                        {t('violations_over_time')}
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                      }}
                    >
                      <Stack
                        justifyContent='flex-end'
                        alignItems='center'
                        height='auto'
                        direction='row'
                      >
                        <Typography variant='body2' sx={{color: ColorsV2.textInfo, marginRight: '10px'}}>
                          {t('milestones')}
                        </Typography>
                        <Switch
                          name='policyViolation'
                          checked={showMileStone.policyViolation}
                          handlechange={() =>
                            handleChange('policyViolation', !showMileStone.policyViolation)
                          }
                        />

                        <Typography variant='body2' sx={{color: ColorsV2.textInfo, marginLeft: '20px'}}>
                          {t('period')}
                        </Typography>
                        <CustomSelect
                          defaultValue={returnDefaultValues(new Date(), new Date(minDate))}
                          value={mileStonesDuration.policyViolation}
                          name='policyViolation'
                          onChange={handleDurationChange}
                          IconComponent={KeyboardArrowDown}
                          inputProps={{
                            'aria-label': 'Language Selector',
                            MenuProps: {
                              PaperProps: {
                                sx: {
                                  backgroundColor: ColorsV2.secondary,
                                  color: ColorsV2.white,
                                  border: `2px solid ${ColorsV2.outline} `,
                                },
                              },
                            },
                          }}
                        >
                          {getMenuItems(new Date(), new Date(minDate)).map(item => (
                            <MenuItem key={item.label} value={item.value} sx={{ fontSize: '0.7em' }}>
                              {item.label}
                            </MenuItem>
                          ))}
                        </CustomSelect>
                        <Button
                          name='IconButton'
                          onClick={() => setIsMilestoneModelOpen(true)}
                          startIcon={<AddIcon style={{ fontSize: '0.9em', color: ColorsV2.white }} />}
                          sx={{
                            ...Buttons.baseStyle.primaryButtonLowPriority,
                            height: '30px',
                            fontSize: '0.8rem',
                            marginLeft: '20px',
                            boxShadow: 0,
                        }}
                        >
                          {t('AddMilestone')}
                        </Button>
                      </Stack>
                    </Grid>
                  </Grid>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      position: 'relative',
                      height: '47vh',
                      marginTop: '3vh',
                    }}
                  >
                    <PolicyViolationChart
                      MileStones={showMileStone.policyViolation ? MileStones : []}
                      duration={mileStonesDuration.policyViolation}
                    />
                  </Box>
                </Grid>
              </Grid>
              <Divider variant='fullWidth' flexItem textAlign='left'
                sx={{
                  "&::before, &::after": {
                    borderColor: ColorsV2.outline,
                  },
                }}
              >
                <Typography variant='h6' sx={{color: ColorsV2.white}}>{t('violations_graphs_header')}</Typography>
              </Divider>
              <Grid container overflow={'hidden'} gap={1}>
                <InformationClassSection />
                <DataSourceSection />
              </Grid>
            </Stack>
            <Stack direction='column' alignItems='center' sx={{marginTop: '20px'}}>
              <ViolationExportMenu
                policyID={withoutTemplate?.policyType}
                policyData={policyData}
                dataSources={structuredAndUnstructuredData?.data}
                setToast={setToast}
                t={t}
              ></ViolationExportMenu>
            </Stack>
          </StyledContainer>
        </Wrapper>
      </Container>
      <Toast
        open={toast.open}
        setOpen={resetToast}
        message={toast.message}
        severity={toast.severity}
      />

      {isMilestoneModelOpen && (
        <MilestoneModel
          setIsMilestoneModelOpen={setIsMilestoneModelOpen}
          setFetchedMultipleMilestoneData={setFetchedMultipleMilestoneData}
          fetchedMultipleMilestoneData={fetchedMultipleMilestoneData}
          multipleMilestoneData={multipleMilestoneData}
          setMultipleMilestoneData={setMultipleMilestoneData}
        />
      )}

      {isMultipleMilestoneModelOpen && (
        <MultipleMilestoneModel
          fetchedMultipleMilestoneData={fetchedMultipleMilestoneData}
          fetchMilestones={() => dispatch(getMilestones())}
          setIsMilestoneModelOpen={setIsMilestoneModelOpen}
          setFetchedMultipleMilestoneData={setFetchedMultipleMilestoneData}
          setIsMultipleMilestoneModelOpen={setIsMultipleMilestoneModelOpen}
          setMultipleMilestoneData={setMultipleMilestoneData}
        />
      )}
    </Box>
  );
};

export default Compliance;
